<template>
  <section class="quiz-container">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-screen">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <h3 class="loading-title">Loading Quiz...</h3>
        <p class="loading-subtitle">Preparing your challenge</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-screen">
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <h3 class="error-title">{{ error.title }}</h3>
        <p class="error-message">{{ error.message }}</p>
        <button class="retry-btn" @click="retryAction" :disabled="isRetrying">
          {{ isRetrying ? 'Retrying...' : 'Try Again' }}
        </button>
      </div>
    </div>

    <!-- Quiz Blocked State -->
    <div v-else-if="isQuizBlocked" class="blocked-screen">
      <div class="blocked-content">
        <div class="blocked-icon">🚫</div>
        <h3 class="blocked-title">Quiz Already Completed</h3>
        <p class="blocked-message">{{ alreadyTakenMessage }}</p>
        <button class="back-btn" @click="navigateBack">
          Go Back
        </button>
      </div>
    </div>

    <!-- Main Quiz Content -->
    <div v-else class="quiz-content">
      <!-- Fixed Header with Progress -->
      <div v-if="quizState !== QUIZ_STATES.WELCOME" class="quiz-header">
        <div class="quiz-progress">
          <div class="progress-bar">
            <div 
              class="progress-fill" 
              :style="{ width: `${progressPercentage}%` }"
            ></div>
          </div>
          <span class="progress-text">
            {{ currentQuestionIndex + 1 }} of {{ questions.length }}
          </span>
        </div>
      </div>

      <!-- Thinking Animation -->
      <div v-if="quizState === QUIZ_STATES.ACTIVE" class="thinking-animation">
        <img src="@/assets/img/home/boy-thinking.gif" alt="Thinking" />
      </div>

      <!-- Welcome Screen -->
      <div v-if="quizState === QUIZ_STATES.WELCOME" class="welcome-screen">
        <div class="welcome-content">
          <div class="welcome-header">
            <h1 class="welcome-title">{{ quizName || 'Quiz Challenge' }}</h1>
            <p class="welcome-subtitle">Answer questions and win WitCoins</p>
          </div>
          <button 
            class="start-btn"
            @click="showRules"
            :disabled="isProcessing"
          >
            {{ isProcessing ? 'Loading...' : 'Start Quiz' }}
          </button>
        </div>
      </div>

      <!-- Rules Modal -->
      <div v-if="quizState === QUIZ_STATES.RULES" class="modal-overlay" @click="handleModalClick">
        <div class="rules-modal" @click.stop>
          <div class="modal-header">
            <h2 class="modal-title">Quiz Rules</h2>
            <button class="close-btn" @click="closeRules">×</button>
          </div>
          
          <div class="modal-content">
            <div class="rules-list">
              <div class="rule-item">
                <span class="rule-number">1</span>
                <span class="rule-text">You have <strong>{{ TIMER_DURATION }} seconds</strong> per question</span>
              </div>
              <div class="rule-item">
                <span class="rule-number">2</span>
                <span class="rule-text">Once selected, answers cannot be changed</span>
              </div>
              <div class="rule-item">
                <span class="rule-number">3</span>
                <span class="rule-text">Questions auto-submit when time expires</span>
              </div>
              <div class="rule-item">
                <span class="rule-number">4</span>
                <span class="rule-text">Earn <strong>10 WitCoins</strong> per correct answer</span>
              </div>
              <div class="rule-item">
                <span class="rule-number">5</span>
                <span class="rule-text">Complete all questions to see your results</span>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="secondary-btn" @click="shareQuiz" :disabled="isProcessing">
              <i class="fas fa-share-alt"></i>
              Share
            </button>
            <button class="secondary-btn" @click="closeRules">
              Cancel
            </button>
            <button 
              class="primary-btn" 
              @click="startQuiz"
              :disabled="isProcessing"
            >
              {{ isProcessing ? 'Starting...' : 'Start Quiz' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Active Quiz -->
      <div v-if="quizState === QUIZ_STATES.ACTIVE && currentQuestion" class="quiz-active">
        <div class="quiz-card">
          <!-- Timer Header -->
          <div class="quiz-card-header">
            <div class="quiz-title">Quiz Challenge</div>
            <div class="timer-container" :class="{ 'timer-warning': timer <= 10, 'timer-critical': timer <= 5 }">
              <div class="timer-label">{{ timer > 0 ? 'Time Left' : 'Time Up' }}</div>
              <div class="timer-value">{{ timer }}</div>
            </div>
          </div>

          <!-- Timer Progress Line -->
          <div class="timer-progress">
            <div 
              class="timer-progress-fill" 
              :style="{ width: `${(timer / TIMER_DURATION) * 100}%` }"
              :class="{ 'progress-warning': timer <= 10, 'progress-critical': timer <= 5 }"
            ></div>
          </div>

          <!-- Question Content -->
          <div class="quiz-card-content">
            <div class="question-section">
              <h2 class="question-text">{{ currentQuestion.text }}</h2>
              <div v-if="currentQuestion.image" class="question-image">
                <img :src="currentQuestion.image" :alt="'Question ' + (currentQuestionIndex + 1)" />
              </div>
            </div>

            <!-- Options -->
            <div class="options-container">
              <div
                v-for="(option, index) in currentQuestion.options"
                :key="`option-${index}-${option}`"
                class="option-item"
                :class="{
                  'option-selected': currentQuestion.selected === option,
                  'option-correct': isAnswerRevealed && currentQuestion.answer === option,
                  'option-incorrect': isAnswerRevealed && currentQuestion.selected === option && currentQuestion.answer !== option,
                  'option-disabled': isOptionDisabled
                }"
                @click="selectOption(option)"
                :aria-label="`Option ${index + 1}: ${option}`"
                role="button"
                tabindex="0"
                @keydown.enter="selectOption(option)"
              >
                <span class="option-text">{{ option }}</span>
                
                <!-- Result Icons -->
                <div v-if="isAnswerRevealed" class="option-icon">
                  <i 
                    v-if="currentQuestion.answer === option" 
                    class="fas fa-check check-icon"
                  ></i>
                  <i 
                    v-else-if="currentQuestion.selected === option && currentQuestion.answer !== option" 
                    class="fas fa-times cross-icon"
                  ></i>
                </div>
              </div>
            </div>
          </div>

          <!-- Quiz Footer -->
          <div class="quiz-card-footer">
            <div class="question-counter">
              Question {{ currentQuestionIndex + 1 }} of {{ questions.length }}
            </div>
            <button 
              v-if="canProceed"
              class="next-btn"
              @click="nextQuestion"
              :disabled="isProcessing"
            >
              {{ isProcessing ? 'Processing...' : (isLastQuestion ? 'Finish Quiz' : 'Next Question') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Results Screen -->
      <div v-if="quizState === QUIZ_STATES.COMPLETED" class="results-screen">
        <div class="results-content">
          <div class="results-header">
            <div class="congrats-icon">
              <img src="@/assets/img/home/cong.png" alt="Congratulations" />
            </div>
            <h2 class="results-title">Congratulations!</h2>
          </div>

          <div class="score-section">
            <div class="score-display">
              <span class="score-value">{{ score }}</span>
              <span class="score-total">/ {{ questions.length }}</span>
            </div>
            <p class="score-text">Questions Answered Correctly</p>
          </div>

          <div class="rewards-section">
            <div v-if="auth.currentUser" class="rewards-authenticated">
              <div class="coins-earned">
                <span class="coins-icon">🪙</span>
                <span class="coins-text">
                  You earned <strong>{{ coinsEarned }} WitCoins</strong>
                </span>
              </div>
              <p class="coins-credited">Coins have been credited to your wallet!</p>
            </div>
            <div v-else class="rewards-guest">
              <div class="login-prompt">
                <span class="coins-icon">🪙</span>
                <span class="login-text">
                  Log in to earn <strong>{{ coinsEarned }} WitCoins</strong>!
                </span>
              </div>
            </div>
          </div>

          <div class="results-actions">
            <button class="secondary-btn" @click="shareResults" :disabled="isProcessing">
              <i class="fas fa-share-alt"></i>
              Share Results
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { db, auth } from "@/main.js";
import { Dialog } from "@capacitor/dialog";
import { Share } from "@capacitor/share";
import { createWebengageEvent } from '../helper';

// Constants
const QUIZ_STATES = {
  WELCOME: 'welcome',
  RULES: 'rules', 
  ACTIVE: 'active',
  COMPLETED: 'completed'
};

const TIMER_DURATION = 30; // seconds
const COINS_PER_CORRECT = 10;

export default {
  name: "QuizComponent",
  data() {
    return {
      // Constants
      QUIZ_STATES,
      TIMER_DURATION,
      
      // Loading and Error States
      isLoading: true,
      error: null,
      isRetrying: false,
      isProcessing: false,
      
      // Quiz State
      quizState: QUIZ_STATES.WELCOME,
      quizName: "",
      questions: [],
      currentQuestionIndex: 0,
      
      // Security State
      isQuizBlocked: false,
      alreadyTakenMessage: "",
      
      // Timer
      timer: 0,
      timerInterval: null,
      
      // Results
      score: 0,
      
      // UI State
      isAnswerRevealed: false,
    };
  },
  
  computed: {
    currentQuestion() {
      return this.questions[this.currentQuestionIndex] || null;
    },
    
    progressPercentage() {
      if (this.questions.length === 0) return 0;
      return ((this.currentQuestionIndex + 1) / this.questions.length) * 100;
    },
    
    isOptionDisabled() {
      return this.timer <= 0 || this.currentQuestion?.attempted || this.isProcessing;
    },
    
    canProceed() {
      return this.currentQuestion?.attempted && !this.isProcessing;
    },
    
    isLastQuestion() {
      return this.currentQuestionIndex === this.questions.length - 1;
    },
    
    coinsEarned() {
      return this.score * COINS_PER_CORRECT;
    },
    
    auth() {
      return auth;
    }
  },
  
  methods: {
    /**
     * Initialize the quiz component with proper security checks
     */
    async initializeQuiz() {
      try {
        this.isLoading = true;
        this.error = null;
        this.isQuizBlocked = false;
        
        // CRITICAL: Check if already taken FIRST, before loading any data
        const alreadyTaken = await this.checkIfAlreadyTaken();
        
        if (alreadyTaken) {
          // Quiz is blocked - user has already taken it
          this.isQuizBlocked = true;
          this.alreadyTakenMessage = "You have already completed this quiz. Each quiz can only be taken once.";
          await this.showAlreadyTakenDialog();
          return; // Exit early - do not load quiz data
        }
        
        // Only fetch quiz data if user hasn't already taken it
        await this.fetchQuizData();
        
      } catch (error) {
        this.handleError('Failed to load quiz', error.message || 'Please check your connection and try again.');
      } finally {
        this.isLoading = false;
      }
    },
    
    /**
     * Fetch quiz data from Firestore (only called if quiz not already taken)
     */
    async fetchQuizData() {
      const quizId = this.$route.params.id;
      if (!quizId) {
        throw new Error('Quiz ID is required');
      }
      
      const doc = await db.collection("quizzes").doc(quizId).get();
      
      if (!doc.exists) {
        throw new Error('Quiz not found');
      }
      
      const quizData = doc.data();
      this.validateQuizData(quizData);
      
      this.quizName = quizData.name || 'Quiz Challenge';
      this.questions = this.parseQuestions(quizData);
    },
    
    /**
     * Validate quiz data structure
     */
    validateQuizData(data) {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid quiz data format');
      }
      
      const questionKeys = Object.keys(data).filter(key => key.startsWith('q'));
      if (questionKeys.length === 0) {
        throw new Error('No questions found in quiz');
      }
      
      // Validate each question
      questionKeys.forEach(key => {
        const question = data[key];
        if (!question.text || !Array.isArray(question.options) || !question.answer) {
          throw new Error(`Invalid question format: ${key}`);
        }
        if (question.options.length < 2) {
          throw new Error(`Question ${key} must have at least 2 options`);
        }
        if (!question.options.includes(question.answer)) {
          throw new Error(`Question ${key} answer not found in options`);
        }
      });
    },
    
    /**
     * Parse questions from quiz data
     */
    parseQuestions(quizData) {
      return Object.keys(quizData)
        .filter(key => key.startsWith('q'))
        .sort() // Ensure consistent order
        .map(key => ({
          ...quizData[key],
          selected: null,
          attempted: false,
        }));
    },
    
    /**
     * Check if user has already taken this quiz
     * Returns true if already taken, false if they can proceed
     */
    async checkIfAlreadyTaken() {
      if (!auth.currentUser) {
        // Guest users can take quiz (but won't earn coins)
        return false;
      }
      
      const quizId = this.$route.params.id;
      
      try {
        const log = await db.collection("quizzes")
          .doc(quizId)
          .collection("logs")
          .doc(auth.currentUser.uid)
          .get();
        
        if (log.exists && log.data().completed) {
          // User has already completed this quiz
          return true;
        }
        
        // Create initial log entry to track attempt
        await db.collection("quizzes")
          .doc(quizId)
          .collection("logs")
          .doc(auth.currentUser.uid)
          .set({
            added: Date.now(),
            value: 0,
            completed: false,
            started: true, // Track that user started the quiz
          }, { merge: true });
        
        return false;
        
      } catch (error) {
        console.error('Error checking quiz completion status:', error);
        // On error, allow quiz but log the issue
        return false;
      }
    },
    
    /**
     * Show rules modal
     */
    showRules() {
      this.quizState = QUIZ_STATES.RULES;
    },
    
    /**
     * Close rules modal
     */
    closeRules() {
      this.quizState = QUIZ_STATES.WELCOME;
    },
    
    /**
     * Handle modal overlay click
     */
    handleModalClick(event) {
      if (event.target === event.currentTarget) {
        this.closeRules();
      }
    },
    
    /**
     * Start the quiz
     */
    async startQuiz() {
      try {
        this.isProcessing = true;
        
        if (this.questions.length === 0) {
          throw new Error('No questions available');
        }
        
        this.quizState = QUIZ_STATES.ACTIVE;
        this.currentQuestionIndex = 0;
        this.startTimer();
        
      } catch (error) {
        this.handleError('Failed to start quiz', error.message);
      } finally {
        this.isProcessing = false;
      }
    },
    
    /**
     * Start the question timer
     */
    startTimer() {
      this.clearTimer();
      this.timer = TIMER_DURATION;
      this.isAnswerRevealed = false;
      
      this.timerInterval = setInterval(() => {
        this.timer--;
        
        if (this.timer <= 0) {
          this.handleTimeUp();
        }
      }, 1000);
    },
    
    /**
     * Handle time up event
     */
    handleTimeUp() {
      this.clearTimer();
      if (!this.currentQuestion.attempted) {
        this.currentQuestion.attempted = true;
        this.revealAnswer();
      }
    },
    
    /**
     * Clear the timer
     */
    clearTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
    
    /**
     * Select an option
     */
    selectOption(option) {
      if (this.isOptionDisabled) return;
      
      this.currentQuestion.selected = option;
      this.currentQuestion.attempted = true;
      this.clearTimer();
      this.revealAnswer();
    },
    
    /**
     * Reveal the correct answer
     */
    revealAnswer() {
      this.isAnswerRevealed = true;
    },
    
    /**
     * Move to next question or finish quiz
     */
    async nextQuestion() {
      try {
        this.isProcessing = true;
        
        if (this.isLastQuestion) {
          await this.finishQuiz();
        } else {
          this.currentQuestionIndex++;
          this.startTimer();
        }
        
      } catch (error) {
        this.handleError('Failed to proceed', error.message);
      } finally {
        this.isProcessing = false;
      }
    },
    
    /**
     * Finish the quiz and show results
     */
    async finishQuiz() {
      this.clearTimer();
      this.calculateScore();
      this.quizState = QUIZ_STATES.COMPLETED;
      
      if (auth.currentUser) {
        await this.creditCoins();
      }
      
      // Auto-redirect after 10 seconds
      setTimeout(() => {
        if (this.quizState === QUIZ_STATES.COMPLETED) {
          this.navigateBack();
        }
      }, 10000);
    },
    
    /**
     * Calculate the final score
     */
    calculateScore() {
      this.score = this.questions.reduce((total, question) => {
        return total + (question.selected === question.answer ? 1 : 0);
      }, 0);
    },
    
    /**
     * Credit coins to user account with duplicate prevention
     */
    async creditCoins() {
      if (!auth.currentUser) return;
      
      const userId = auth.currentUser.uid;
      const quizId = this.$route.params.id;
      const coinsToCredit = this.coinsEarned;
      
      try {
        // SECURITY: Double-check completion status before crediting coins
        const logRef = db.collection("quizzes")
          .doc(quizId)
          .collection("logs")
          .doc(userId);
        
        const currentLog = await logRef.get();
        
        if (currentLog.exists && currentLog.data().completed) {
          console.warn('Attempted to credit coins for already completed quiz');
          return; // Exit early - don't credit coins again
        }
        
        // Use a transaction to ensure atomicity and prevent race conditions
        await db.runTransaction(async (transaction) => {
          // Re-read the log within the transaction
          const logDoc = await transaction.get(logRef);
          
          if (logDoc.exists && logDoc.data().completed) {
            throw new Error('Quiz already completed within transaction');
          }
          
          // Add coins transaction
          const coinsRef = db.collection("users")
            .doc(userId)
            .collection("coins")
            .doc(); // Generate unique ID
          
          transaction.set(coinsRef, {
            created: Date.now(),
            from: "quiz",
            value: coinsToCredit,
            quizId: quizId,
            transactionId: coinsRef.id, // Track transaction ID
          });
          
          // Update quiz log as completed
          transaction.set(logRef, {
            updated: Date.now(),
            value: coinsToCredit,
            completed: true,
            score: this.score,
            completedAt: Date.now(),
          }, { merge: true });
          
          // Add notification
          const notificationRef = db.collection("users")
            .doc(userId)
            .collection("notifications")
            .doc();
          
          transaction.set(notificationRef, {
            created: Date.now(),
            action: "",
            text: `You earned ${coinsToCredit} coins for completing the quiz!`,
            img: "https://firebasestorage.googleapis.com/v0/b/witblox-5ae68.appspot.com/o/profile%2Fd4ae4c4ced1234134c5d?alt=media&token=c1f8ce0d-cb76-48eb-8898-a1bc6c070864",
            link: "/transactions",
            user: userId,
          });
        });
        
        // Track analytics event
        createWebengageEvent({
          uid: userId,
          eventName: "Quiz Completed",
          eventData: {
            witcoins: coinsToCredit,
            score: this.score,
            name: this.quizName,
            quizId: quizId,
          }
        });
        
      } catch (error) {
        console.error('Error crediting coins:', error);
        // Don't throw error to avoid disrupting user experience
      }
    },
    
    /**
     * Share quiz with friends
     */
    async shareQuiz() {
      try {
        await Share.share({
          title: "WitBlox Quiz Challenge",
          text: `Challenge yourself with this quiz: ${this.quizName}. Can you beat my score?`,
          url: `https://app.witblox.com/quiz/${this.$route.params.id}`,
          dialogTitle: "Share Quiz Challenge",
        });
      } catch (error) {
        console.error("Error sharing quiz:", error);
      }
    },
    
    /**
     * Share quiz results
     */
    async shareResults() {
      try {
        const scoreText = `I scored ${this.score}/${this.questions.length} on "${this.quizName}" and earned ${this.coinsEarned} WitCoins!`;
        
        await Share.share({
          title: "My Quiz Results",
          text: `${scoreText} Challenge yourself and see if you can beat my score!`,
          url: `https://app.witblox.com/quiz/${this.$route.params.id}`,
          dialogTitle: "Share Your Results",
        });
      } catch (error) {
        console.error("Error sharing results:", error);
      }
    },
    
    /**
     * Show already taken dialog and handle navigation
     */
    async showAlreadyTakenDialog() {
      try {
        const message = this.alreadyTakenMessage || "You have already completed this quiz. Each quiz can only be taken once.";
        
        await Dialog.alert({
          title: "Quiz Already Completed",
          message: message + "\n\nYou can explore other quizzes or check your transaction history to see your earned coins.",
        });
      } catch (error) {
        console.error('Error showing dialog:', error);
        // Fallback: Try to show a browser alert if Capacitor Dialog fails
        try {
          alert("Quiz Already Completed\n\n" + (this.alreadyTakenMessage || "You have already completed this quiz. Each quiz can only be taken once."));
        } catch (fallbackError) {
          console.error('Fallback alert also failed:', fallbackError);
        }
      } finally {
        // Always navigate away after showing dialog
        this.navigateBack();
      }
    },
    
    /**
     * Navigate back to previous page
     */
    navigateBack() {
      if (auth.currentUser) {
        this.$router.back();
      } else {
        this.$router.push('/login?redirect=' + encodeURIComponent(this.$route.fullPath));
      }
    },
    
    /**
     * Handle errors
     */
    handleError(title, message) {
      this.error = { title, message };
      this.isLoading = false;
      this.isProcessing = false;
    },
    
    /**
     * Retry failed action
     */
    async retryAction() {
      this.isRetrying = true;
      this.error = null;
      
      try {
        await this.initializeQuiz();
      } catch (error) {
        this.handleError('Retry failed', 'Please check your connection and try again.');
      } finally {
        this.isRetrying = false;
      }
    },
  },
  
  async beforeMount() {
    await this.initializeQuiz();
  },
  
  beforeUnmount() {
    this.clearTimer();
  },
  
  // Handle browser refresh/navigation
  beforeRouteLeave(to, from, next) {
    this.clearTimer();
    next();
  },
  
  // Route guard to prevent re-entry if quiz is blocked
  async beforeRouteEnter(to, from, next) {
    // This guard runs before the component is created
    // We need to check if the user has already taken this quiz
    if (auth.currentUser) {
      const quizId = to.params.id;
      try {
        const log = await db.collection("quizzes")
          .doc(quizId)
          .collection("logs")
          .doc(auth.currentUser.uid)
          .get();
        
        if (log.exists && log.data().completed) {
          // User has already completed this quiz - show message and redirect
          console.warn('Blocked route access to completed quiz:', quizId);
          
          // Import Dialog for showing the message
          const { Dialog } = await import('@capacitor/dialog');
          
          // Show the alert dialog
          await Dialog.alert({
            title: "Quiz Already Completed",
            message: "You have already completed this quiz. Each quiz can only be taken once. You will be redirected to the home page.",
          });
          
          // Redirect to home page
          next('/');
          return;
        }
      } catch (error) {
        console.error('Error in route guard:', error);
        // Continue on error to avoid blocking legitimate access
      }
    }
    
    // Allow access
    next();
  },
};
</script>

<style scoped>
/* Reset and Base Styles */
* {
  box-sizing: border-box;
}

.quiz-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: relative;
  overflow-x: hidden;
}

/* Loading Screen */
.loading-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
}

.loading-content {
  text-align: center;
  color: white;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
}

.loading-subtitle {
  font-size: 1rem;
  opacity: 0.8;
  margin: 0;
}

/* Error Screen */
.error-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
}

.error-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.error-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem;
}

.error-message {
  color: #6b7280;
  margin: 0 0 1.5rem;
  line-height: 1.5;
}

.retry-btn {
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.retry-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Blocked Screen */
.blocked-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
}

.blocked-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  border: 2px solid #fee2e2;
}

.blocked-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.blocked-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #dc2626;
  margin: 0 0 0.5rem;
}

.blocked-message {
  color: #6b7280;
  margin: 0 0 1.5rem;
  line-height: 1.5;
}

.back-btn {
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-btn:hover:not(:disabled) {
  background: #4b5563;
  transform: translateY(-1px);
}

.back-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Quiz Header */
.quiz-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  z-index: 100;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.quiz-progress {
  display: flex;
  align-items: center;
  gap: 1rem;
  max-width: 600px;
  margin: 0 auto;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
}

/* Thinking Animation */
.thinking-animation {
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
}

.thinking-animation img {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

/* Welcome Screen */
.welcome-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
}

.welcome-content {
  text-align: center;
  color: white;
  max-width: 500px;
}

.welcome-header {
  margin-bottom: 3rem;
}

.welcome-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 1rem;
  background: linear-gradient(45deg, #fff, #e0e7ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.welcome-subtitle {
  font-size: 1.125rem;
  opacity: 0.9;
  margin: 0;
}

.start-btn {
  background: white;
  color: #3b82f6;
  border: none;
  border-radius: 12px;
  padding: 1rem 2rem;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

.start-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 15px 35px -5px rgba(0, 0, 0, 0.15);
}

.start-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(4px);
}

.rules-modal {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: modalEnter 0.2s ease-out;
}

@keyframes modalEnter {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-content {
  padding: 1.5rem;
}

.rules-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.rule-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.rule-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.rule-text {
  color: #374151;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.primary-btn, .secondary-btn {
  border: none;
  border-radius: 8px;
  padding: 0.625rem 1.25rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.primary-btn {
  background: #3b82f6;
  color: white;
}

.primary-btn:hover:not(:disabled) {
  background: #2563eb;
}

.secondary-btn {
  background: #f3f4f6;
  color: #374151;
}

.secondary-btn:hover:not(:disabled) {
  background: #e5e7eb;
}

.primary-btn:disabled, .secondary-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Quiz Active */
.quiz-active {
  padding: 120px 1rem 2rem;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quiz-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 600px;
  width: 100%;
  overflow: hidden;
}

.quiz-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
}

.quiz-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.timer-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #dbeafe;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  transition: all 0.2s ease;
}

.timer-container.timer-warning {
  background: #fef3c7;
  border-color: #fbbf24;
}

.timer-container.timer-critical {
  background: #fee2e2;
  border-color: #f87171;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.timer-label {
  font-size: 0.875rem;
  color: #374151;
}

.timer-value {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  min-width: 24px;
  text-align: center;
}

.timer-progress {
  height: 3px;
  background: #f3f4f6;
  position: relative;
  overflow: hidden;
}

.timer-progress-fill {
  height: 100%;
  background: #3b82f6;
  transition: width 1s linear;
}

.timer-progress-fill.progress-warning {
  background: #f59e0b;
}

.timer-progress-fill.progress-critical {
  background: #ef4444;
}

.quiz-card-content {
  padding: 2rem;
}

.question-section {
  margin-bottom: 2rem;
}

.question-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.4;
  margin: 0 0 1rem;
}

.question-image {
  margin-top: 1rem;
}

.question-image img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.options-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.option-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.option-item:hover:not(.option-disabled) {
  background: #e0f2fe;
  border-color: #0284c7;
  transform: translateY(-1px);
}

.option-item.option-selected {
  background: #dbeafe;
  border-color: #3b82f6;
}

.option-item.option-correct {
  background: #dcfce7;
  border-color: #16a34a;
}

.option-item.option-incorrect {
  background: #fee2e2;
  border-color: #dc2626;
}

.option-item.option-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.option-text {
  flex: 1;
  font-weight: 500;
  color: #374151;
}

.option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.check-icon {
  color: #16a34a;
}

.cross-icon {
  color: #dc2626;
}

.quiz-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.question-counter {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.next-btn {
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.next-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.next-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Results Screen */
.results-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
}

.results-content {
  background: white;
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.results-header {
  margin-bottom: 2rem;
}

.congrats-icon img {
  width: 120px;
  height: 120px;
  margin-bottom: 1rem;
}

.results-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.score-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
}

.score-display {
  font-size: 3rem;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 0.5rem;
}

.score-total {
  color: #6b7280;
}

.score-text {
  font-size: 1rem;
  color: #374151;
  margin: 0;
}

.rewards-section {
  margin-bottom: 2rem;
}

.coins-earned, .login-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 1.125rem;
  margin-bottom: 0.5rem;
}

.coins-icon {
  font-size: 1.5rem;
}

.coins-credited {
  font-size: 0.875rem;
  color: #16a34a;
  margin: 0;
}

.login-text {
  color: #374151;
}

.results-actions {
  display: flex;
  justify-content: center;
}

/* Responsive Design */
@media (max-width: 640px) {
  .quiz-header {
    padding: 0.75rem 1rem;
  }
  
  .quiz-progress {
    gap: 0.75rem;
  }
  
  .welcome-title {
    font-size: 2rem;
  }
  
  .quiz-active {
    padding: 100px 0.75rem 1rem;
  }
  
  .quiz-card-header {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .timer-container {
    justify-content: space-between;
  }
  
  .quiz-card-content {
    padding: 1.5rem;
  }
  
  .question-text {
    font-size: 1.125rem;
  }
  
  .quiz-card-footer {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .next-btn {
    width: 100%;
  }
  
  .rules-modal {
    margin: 1rem;
    max-height: calc(100vh - 2rem);
  }
  
  .modal-footer {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .primary-btn, .secondary-btn {
    width: 100%;
    justify-content: center;
  }
  
  .results-content {
    padding: 2rem 1.5rem;
  }
  
  .score-display {
    font-size: 2.5rem;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus styles */
.option-item:focus,
.start-btn:focus,
.primary-btn:focus,
.secondary-btn:focus,
.next-btn:focus,
.retry-btn:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Print styles */
@media print {
  .quiz-container {
    background: white !important;
  }
  
  .thinking-animation {
    display: none;
  }
}
</style>
